import React, {
  useState,
  useImperativeHandle,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { HelloProduct } from "../../types/backend";
import { Web3DTracker } from "hello-mono-sdk/packages/web-3d";
import { ARTracker } from "hello-mono-sdk/packages/ar-sdk";
import { RuleJsonType } from "hello-mono-sdk/packages/core";
import { useProduct } from "../../hooks/product";
import "./Viewer.scss";
import "../../styles/global.scss";
import { MenuItem, ViewerRefType } from "../../types/frontend";
import Tracker from "../../HelloARTracker";
import { API } from "../../api/products";
import ArButton from "../ArButton/ArButton";
import {
  does3dDataExist,
  doesArDataExist,
  doesSpecDataExist,
  doesSpinDataExist,
} from "../../utils/contentType";
import Viewer3D from "../Viewer3D";
import SpinViewerReact from "../SpinViewer/SpinViewer";
import Specs from "../Specs";
import { addStyle, addVariableToRoot, loadFont } from "../../utils/styles";
import { useConfigurations } from "../../hooks/configuration";

interface ViewerProps {
  id?: string;
  data?: HelloProduct;
  showMenuRules?: boolean;
  server: "PROD" | "DEV";
  onProgress?: (progress: number) => void;
  onError?: (errorMessage: string) => void;
  onSceneLoaded?: () => void;
  ar?: boolean;
  muteAudio?: boolean;
  disableSpin?: boolean;
  disable3d?: boolean;
  disableSpec?: boolean;
}

const urlParams = new URLSearchParams(window.location.search);
const viewerId = urlParams.get("viewer_id");
const parentSessionId = urlParams.get("parent_session_id");

const HelloViewer = React.forwardRef<ViewerRefType, ViewerProps>(
  (props, ref) => {
    const [currentMode, setCurrentMode] = useState<"3D" | "360" | "AR" | null>(
      "3D"
    );
    const [shouldHideArButton, setShouldHideArButton] = useState(false);
    const [SpecsVisible, setSpecsVisible] = useState<boolean>(false);
    const [isFeaturesActive, setIsFeaturesActive] = useState(false);

    const viewerRef = useRef<ViewerRefType>(null);

    Tracker.initTracker(
      props.server,
      parentSessionId && parentSessionId.length > 0
        ? parentSessionId
        : undefined
    );
    API.setEnvironment(props.server);
    Web3DTracker.setTracker(Tracker.getTracker());
    ARTracker.setTracker(Tracker.getTracker());
    if (viewerId && viewerId.length > 0) {
      Tracker.getTracker()?.curentProductSession?.legacyTracker?.setViewerId(
        viewerId
      );
    }

    const {
      isLoading,
      isError,
      error,
      data: productData,
    } = useProduct({ id: props.id, data: props.data });

    const {
      stylesJSON,
      bgJSON,
      productConfigurations,
      isLoading: isLoadingConfig,
    } = useConfigurations({
      product: productData,
    });

    useEffect(() => {
      if (window.innerWidth > 600) setSpecsVisible(true);
    }, []);

    useEffect(() => {
      if (stylesJSON && stylesJSON.fonts) {
        stylesJSON.fonts.forEach((fontData, index) => {
          const fontName: string | undefined = fontData.font_name;
          const fontUrl: string | undefined = fontData.font_url;
          if (fontName?.length && fontUrl?.length) {
            loadFont(fontName, fontUrl, index === 0);
          }
        });
      }

      if (stylesJSON && stylesJSON.custom_css) {
        const css: string | undefined = stylesJSON.custom_css;
        if (css) {
          addStyle(css, "prepend");
        }
      }
    }, [JSON.stringify(stylesJSON)]);

    useEffect(() => {
      if (productConfigurations && productConfigurations.customStyles) {
        const css: string | undefined = productConfigurations.customStyles;
        if (css) {
          addStyle(css, "prepend");
        }
      }
    }, [JSON.stringify(productConfigurations)]);

    useEffect(() => {
      if (bgJSON) {
        if (bgJSON.backgroundColor?.length) {
          addVariableToRoot("backgroundColor", bgJSON.backgroundColor);
          if (bgJSON.gradientColor?.length) {
            addVariableToRoot("gradientColor", bgJSON.gradientColor);
          } else {
            addVariableToRoot("gradientColor", bgJSON.backgroundColor);
          }
        }
      }
    }, [JSON.stringify(bgJSON)]);

    useEffect(() => {
      if (isError) {
        props.onError && props.onError(error);
      }
    }, [isError, error, props.onError]);

    useImperativeHandle(
      ref,
      () => {
        return {
          runCustomRule: (args: { rule: RuleJsonType; node: any, completionHandler?:any }) => {
            viewerRef.current?.runCustomRule(args);
          },
          getCore3D: () => {
            const core = viewerRef.current?.getCore3D();
            return core;
          },
          getViewer: () => {
            const viewer = viewerRef.current?.getViewer()
            return viewer;
          }
        };
      },
      []
    );

    const getMenuRules = (): MenuItem[] => {
      let ruleJson = productData?.rules?.json;
      let rules: MenuItem[] = [];

      if (ruleJson) {
        const parsedRules = JSON.parse(ruleJson)?.rules;

        if (parsedRules && Array.isArray(parsedRules)) {
          const menuRules = parsedRules.filter((rule) => rule.type === "Menu");
          const subMenuRules = parsedRules.filter(
            (rule) => rule.type === "SubMenu"
          );

          menuRules.forEach((rule: RuleJsonType) => {
            rules.push({
              id: rule.id,
              text: rule.iconText,
              rule: rule,
            });
          });

          subMenuRules.forEach((rule: RuleJsonType) => {
            if (rule.parameters.parentMenu) {
              const parentItem = rules.find(
                (item) => item.text === rule.parameters.parentMenu
              );

              if (parentItem) {
                if (!parentItem.children) {
                  parentItem.children = [];
                }

                parentItem.children.push({
                  id: rule.id + "+child",
                  text: rule.iconText,
                  rule: rule,
                });
              } else {
                console.warn(
                  `Parent menu not found for submenu: ${rule.iconText}`
                );
              }
            }
          });
        }
      }
      return rules;
    };

    const showArButton = useCallback(() => {
      setShouldHideArButton(false);
    }, []);

    const hideArButton = useCallback(() => {
      setShouldHideArButton(true);
    }, []);

    const is3dData = productData
      ? does3dDataExist(productData) && !props.disable3d
      : false;
    const isSpecData = productData
      ? doesSpecDataExist(productData) && !props.disableSpec
      : false;
    const isSpinData = productData
      ? doesSpinDataExist(productData) && !props.disableSpin
      : false;
    const isArData = productData
      ? props.ar && doesArDataExist(productData)
      : false;

    const menuItems = getMenuRules();

    if (isLoading || isLoadingConfig) {
      return (
        <div className="helloar-reserved">
          <div className="loader-container">
            <div className="loader-wrapper">
              <div className="loader-outline">
                <div className="loader-progress" style={{ width: `${0}%` }} />

                <span className="loader-text">0%</span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (isError) {
      return (
        <div className="helloar-reserved">
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%,-50%)",
              backgroundColor: "rgba(255,255,255,0.5)",
              zIndex: 100000,
            }}
          >
            <span>Error: {error}</span>
          </div>
        </div>
      );
    }

    if (!(is3dData && isSpinData)) {
      if (is3dData) {
        if (currentMode !== "3D" && currentMode !== "AR") {
          setCurrentMode("3D");
        }
      } else if (isSpinData) {
        if (currentMode !== "360" && currentMode !== "AR") {
          setCurrentMode("360");
        }
      } else {
        if (currentMode) {
          setCurrentMode(null);
        }
      }
    }
    if (productData) {
      return (
        <div className="helloar-reserved">
          {!isSpinData && !is3dData && <p>No Data Found</p>}
          {currentMode === "3D" && (
            <div className="specs-viewer-container">
              <div className={`viewer ${isSpecData ? "" : "full-width"}`}>
                {is3dData && isSpinData && (
                  <div className="switch-container">
                    <div className="switch-buttons-container">
                      <button
                        onClick={() => {
                          if (currentMode === "3D") {
                            setCurrentMode("360");
                          }
                        }}
                      >
                        Switch To 360
                      </button>
                    </div>
                  </div>
                )}
                <Viewer3D
                  specMode={isSpecData && SpecsVisible ? "SPEC" : ""}
                  ref={viewerRef}
                  product={productData}
                  onProgress={props.onProgress}
                  onSceneLoaded={props.onSceneLoaded}
                  muteAudio={props.muteAudio}
                  showMenu={!!props.showMenuRules}
                  menuItems={menuItems}
                  hideArButton={hideArButton}
                  showArButton={showArButton}
                  setSpecsVisible={setSpecsVisible}
                  setIsFeaturesActive={setIsFeaturesActive}
                />
              </div>
              {isSpecData && (
                <Specs
                  product={productData}
                  specsVisible={SpecsVisible}
                  setSpecsVisible={setSpecsVisible}
                  isFeaturesActive={isFeaturesActive}
                />
              )}
            </div>
          )}

          {currentMode === "360" && (
            <div className="specs-viewer-container">
              <div className={`viewer ${isSpecData ? "" : "full-width"}`}>
                {is3dData && isSpinData && (
                  <div className="switch-container">
                    <div className="switch-buttons-container">
                      <button
                        onClick={() => {
                          if (currentMode === "360") {
                            setCurrentMode("3D");
                          }
                        }}
                      >
                        Switch To 3D
                      </button>
                    </div>
                  </div>
                )}
                <SpinViewerReact
                  product={productData}
                  hideArButton={hideArButton}
                  showArButton={showArButton}
                />
              </div>
              {isSpecData && (
                <Specs
                  product={productData}
                  specsVisible={SpecsVisible}
                  setSpecsVisible={setSpecsVisible}
                  isFeaturesActive={isFeaturesActive}
                />
              )}
            </div>
          )}

          {isArData && !shouldHideArButton && (
            <ArButton
              product={productData}
              beforeArStart={() => {
                setCurrentMode("AR");
              }}
              margin={
                props.showMenuRules && menuItems.length > 0
                  ? "0 0 50px 0"
                  : undefined
              }
              onArEnded={() => {
                if (is3dData) {
                  setCurrentMode("3D");
                } else if (isSpinData) {
                  setCurrentMode("360");
                }
              }}
            />
          )}

          {currentMode === "AR" && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <p>IN AR MODE</p>
            </div>
          )}
        </div>
      );
    }

    return <p>Unexpected Error</p>;
  }
);

export default HelloViewer;




// <button onClick={
//   ()=>{
//     console.log("CLICKED")
//     onMoveCamera()
//   }
// }>
//   hi
// </button>

// const onMoveCamera = async() => {
// //  const  pos = "LEFT_FRONT_DIAGONAL";
// const pos = "TOP"
// const cameraManager = viewerRef?.current?.getCore3D().getCameraManager();
// cameraManager?.moveToDirection(pos);
// cameraManager?.zoomIn(25);
// console.log(cameraManager?.getCameraPosition())
// cameraManager?.updateCamera({
// position:{
// x: 0, y: 1.494513881042166, z: 0.0000014945803111435116
// }
// })
// console.log(await getImageFromScene())
// downloadImage(await getImageFromScene(), "powerdock")
// };

// const getImageFromScene = async () => {
// const base64Image = await viewerRef?.current?.getCore3D().saveAsImage();
// return base64Image;
// };


// const downloadImage = (base64Data: string, filename: string) => {
// // Create a link element
// const link = document.createElement('a');

// // Set the href attribute to the base64 data
// link.href = base64Data;

// // Set the download attribute to the filename
// link.download = filename;

// // Create a click event
// const clickEvent = new MouseEvent('click');

// // Dispatch the click event on the link
// link.dispatchEvent(clickEvent);
// };



